import { useEffect, useState } from "react";

/**
 * Marca la sección visible en la navegación.
 * Usa el punto medio del viewport como referencia: es lo que mejor
 * coincide con la percepción del usuario en una página larga con
 * secciones de altura muy distinta.
 */
export function useScrollSpy(ids, offset = 120) {
  const [active, setActive] = useState(ids[0]);

  useEffect(() => {
    const onScroll = () => {
      const line = window.scrollY + offset;
      let current = ids[0];

      ids.forEach((id) => {
        const node = document.getElementById(id);
        if (node && node.offsetTop <= line) current = id;
      });

      // Al final de la página, activa siempre la última sección.
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 4
      ) {
        current = ids[ids.length - 1];
      }

      setActive(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids, offset]);

  return active;
}

/**
 * Normaliza la ruta antes de compararla.
 * El build publica /unete como carpeta (unete/index.html), así que el
 * hosting puede entregarla como "/unete", "/unete/" o "/unete/index.html".
 * Sin normalizar, la comparación exacta fallaba en producción y la
 * navegación del header quedaba muerta en esa página.
 */
export function normalizePath(pathname = window.location.pathname) {
  return pathname.replace(/\/index\.html?$/i, "").replace(/\/+$/, "") || "/";
}

/** ¿Estamos en la página de Únete a FSG? */
export function isJoinPath(pathname) {
  return normalizePath(pathname) === "/unete";
}

/** Desplaza a una sección respetando prefers-reduced-motion. */
export function scrollToSection(id) {
  const node = document.getElementById(id);
  if (!node || typeof node.scrollIntoView !== "function") return;

  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  node.scrollIntoView({
    behavior: prefersReduced ? "auto" : "smooth",
    block: "start",
  });

  // Mantiene la URL compartible sin provocar un salto adicional.
  if (window.history?.replaceState) {
    window.history.replaceState(null, "", `#${id}`);
  }
}

/** Detecta si la página se desplazó, para condensar el header. */
export function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}
